import { CSSProperties, Fragment, ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useDispatch, useSelector } from "react-redux"
import Api from "../../api/api"
import { useSkuClaims } from "../../redux/claimsSlice"
import { productFetchThunk } from "../../redux/productsSlice"
import { AppDispatch, RootState } from "../../redux/store"
import { meSelector } from "../../redux/globalSlice"
import { Claim, ClaimType, GuestOrder, GuestOrderStats, GuestOrderStatus, NfaOrderStatus, SkuOrder, SkuOrderStats, LicenseType, licenseTypeLabels, Offering, Product, RedemptionCodeBatch, RedemptionCodeLookup, Sku, SkuPromotion, SkuVersion, SkuVersionStatus, skuVersionStatusLabels, UserRole } from "../../api/models"
import Spinner from "../widgets/Spinner"
import UserChip from "../widgets/UserChip"
import OrgChip, { useOrganization } from "../widgets/OrgChip"
import CardImage from "../widgets/CardImage"
import { FetchableStateStatus } from "../../redux/fetchableState"
import { useGenerateCodesModal } from "../GenerateCodesModal"
import { DeleteOfferingDialog, OfferingDialog } from "../OfferingDialog"
import { PromotionActionDialog, PromotionDialog } from "../PromotionDialog"
import EditSkuDialog, { DeleteSkuDialog } from "../EditSkuDialog"
import SkuName from "../widgets/SkuName"
import CopyableId from "../widgets/CopyableId"
import TabCard from "../widgets/TabCard"
import { AddVersionDialog, DeleteVersionDialog } from "../AddVersionDialog"
import { PromoteVersionDialog } from "../VersionLifecycleDialogs"
import { GrantSkuDialog } from "../GrantSkuDialog"
import { AmendGuestOrderEmailDialog, DepositGuestOrderDialog } from "../GuestOrderDialogs"
import EntitlementsTable from "../EntitlementsTable"
import { bundleIcon, freeIcon, oneTimeIcon, rewardIcon, singleIcon, subscriptionIcon } from "../widgets/pillIcons"
import { buildImageSrcSet } from "../widgets/imageSrcSet"
import { formatPrice } from "../widgets/UpgradeInfo"
import { billingPeriodLabel } from "./SubscriptionPage"
import { backdropDismiss } from "../../utils/backdropDismiss"

const offeringMeta: Record<Offering["paymentType"], { label: string, icon: ReactNode }> = {
  one_time: { label: "One-time purchase", icon: oneTimeIcon },
  subscription: { label: "Subscription", icon: subscriptionIcon },
  free: { label: "Free", icon: freeIcon },
  reward: { label: "Reward", icon: rewardIcon },
}

const offeringOrder: Record<Offering["paymentType"], number> = {
  one_time: 0,
  subscription: 1,
  free: 2,
  reward: 3,
}

// newest entitlements shown inline; the full list lives at /skus/:id/users
const USERS_PREVIEW_COUNT = 5

// storeVariantId is undefined until the admin API ships it and null on
// offerings without a store variant — hidden in both cases
// isBundle: the sku licenses more than one product (any version) — the
// upgrade pricing mode only means something then
function offeringDetails(offering: Offering, isBundle: boolean): string[] {
  const details: string[] = []
  if (offering.paymentType === "subscription") {
    details.push(`billed ${billingPeriodLabel(offering.intervalUnit, offering.intervalCount).toLowerCase()}`)
    if (offering.trialDays != null && offering.trialDays > 0)
      details.push(`${offering.trialDays}-day trial`)
  }
  if (offering.rewardPoints > 0)
    details.push(`+${offering.rewardPoints} points${offering.paymentType === "subscription" ? " per renewal" : ""}`)
  if (offering.licenseExpiryDays != null)
    details.push(`licenses expire after ${offering.licenseExpiryDays} days`)
  if (isBundle && offering.upgradePricing === "count")
    details.push("upgrades priced by product count")
  if (offering.dynamicPricing != null) {
    const n = offering.discounts?.length ?? 0
    details.push(offering.dynamicPricing === "fixed"
      ? `crossgrade: $${((offering.dynamicPriceCents ?? 0) / 100).toFixed(2)} for owners of ${n === 1 ? "1 product" : `any of ${n} products`}`
      : `crossgrade: ${n} product${n === 1 ? "" : "s"} (${offering.dynamicPricing === "percent" ? "% off" : "$ off"})`)
  }
  // if (offering.storeVariantId != null)
  //   details.push(`variant ${offering.storeVariantId}`)
  return details
}

const rowActionStyle: CSSProperties = {
  background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt"
}

function OfferingRow({ offering, isBundle, onEdit, onDelete, onRestore, restoring }: {
  offering: Offering
  isBundle: boolean
  onEdit: () => void
  onDelete: () => void
  onRestore: () => void
  restoring: boolean
}) {
  const meta = offeringMeta[offering.paymentType]
  const details = offeringDetails(offering, isBundle)
  const discontinued = offering.discontinued === true

  return (
    <div className="license-group" style={discontinued ? { opacity: 0.6 } : undefined}>
      <div className="product-group-header">
        <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {meta.icon}
          {meta.label}
          {discontinued && <span className="badge">Discontinued</span>}
        </h2>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
          {details.length > 0 && (
            <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
              {details.join(" · ")}
            </span>
          )}
          {/* reward offerings price in points (priceCents holds the count) */}
          {offering.paymentType === "reward" && (
            <span style={{ whiteSpace: "nowrap" }}>{offering.priceCents} pts</span>
          )}
          {offering.paymentType !== "free" && offering.paymentType !== "reward" && (
            <span style={{ whiteSpace: "nowrap" }}>{formatPrice(offering.priceCents / 100)}</span>
          )}
          <button className="card-link" style={rowActionStyle} onClick={onEdit}>Edit</button>
          {discontinued ? (
            <button className="card-link" style={rowActionStyle} onClick={onRestore} disabled={restoring}>
              {restoring ? "Restoring…" : "Restore"}
            </button>
          ) : (
            <button className="card-link-danger" style={rowActionStyle} onClick={onDelete}>Delete</button>
          )}
        </div>
      </div>
    </div>
  )
}

// newest batches shown inline; "More" pages the rest in place (there is no
// dedicated batches page to link to)
const CODES_PREVIEW_COUNT = 3
const CODES_PAGE_SIZE = 10

function CodeBatchRow({ batch, onRevoke, onUnrevoke, unrevoking }: {
  batch: RedemptionCodeBatch
  onRevoke: () => void
  onUnrevoke: () => void
  unrevoking: boolean
}) {
  const date = new Date(batch.createdAt).toLocaleDateString()
  const revoked = batch.revokedAt !== null
  const fullyRedeemed = batch.codeCount > 0 && batch.redeemedCount >= batch.codeCount
  const details: string[] = [date]
  // the version the batch's codes grant (fixed at minting time); the != null
  // guards a backend that predates versioned claims
  if (batch.skuVersion != null) details.push(`v${batch.skuVersion}`)
  details.push(`${batch.codeCount} code${batch.codeCount === 1 ? "" : "s"}`)
  details.push(`${batch.redeemedCount} redeemed`)
  if (batch.rewardPoints > 0) details.push(`+${batch.rewardPoints} points`)
  // what one code is worth (standard batches only; null = minted before the field)
  if (batch.licenseType === LicenseType.Standard && batch.codeValueCents != null)
    details.push(`${formatPrice(batch.codeValueCents / 100)} per code`)
  // minted iLok-disabled: redeemed entitlements get no "Get iLok license"
  if (batch.ilokEnabled === false) details.push("no iLok")
  if (batch.licenseExpiryDays != null) details.push(`licenses expire after ${batch.licenseExpiryDays} days`)
  // an expired batch gets a pill instead of the details entry (date in its tooltip)
  if (batch.expiresAt !== null && !batch.expired)
    details.push(`expires ${new Date(batch.expiresAt).toLocaleDateString()}`)
  // who/when/why lives in the pill's tooltip — the row itself stays uncluttered
  const revokedTooltip = revoked
    ? [
        `Revoked ${new Date(batch.revokedAt!).toLocaleDateString()}`,
        batch.revokedBy ? `by ${batch.revokedBy}` : null,
        batch.revokedReason ? `— ${batch.revokedReason}` : null,
      ].filter(Boolean).join(" ")
    : undefined

  return (
    <div className="license-group" style={revoked || batch.expired || fullyRedeemed ? { opacity: 0.6 } : undefined}>
      <div className="product-group-header">
        <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {batch.comment ?? <span style={{ fontStyle: "italic", fontWeight: "normal", opacity: 0.7 }}>Unnamed</span>}
          {batch.licenseType !== LicenseType.Standard && <span className="badge">{licenseTypeLabels[batch.licenseType]}</span>}
          {fullyRedeemed && <span className="badge">Redeemed</span>}
          {batch.expired && (
            <span className="badge" style={{ cursor: "help" }} title={`Expired ${new Date(batch.expiresAt!).toLocaleDateString()}`}>Expired</span>
          )}
          {revoked && <span className="badge" style={{ cursor: "help" }} title={revokedTooltip}>Revoked</span>}
        </h2>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
            {details.join(" · ")}
          </span>
          <UserChip userId={batch.userId} style={{ marginLeft: "auto" }} />
          {revoked ? (
            <button className="card-link" style={rowActionStyle} onClick={onUnrevoke} disabled={unrevoking}>
              {unrevoking ? "Restoring…" : "Undo revoke"}
            </button>
          ) : (
            <button className="card-link-danger" style={rowActionStyle} onClick={onRevoke}>Revoke</button>
          )}
        </div>
      </div>
    </div>
  )
}

// Confirmation dialog for revoking one code batch: its unredeemed codes stop
// redeeming (already-redeemed grants are untouched). Reversible via the row's
// Undo revoke button, so no extra warning ceremony.
function RevokeBatchDialog({ batch, skuId, accessToken, onClose, onRevoked }: {
  batch: RedemptionCodeBatch
  skuId: string
  accessToken: string
  onClose: () => void
  onRevoked: (batch: RedemptionCodeBatch) => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [reason, setReason] = useState("")
  const [step, setStep] = useState<"confirm" | "revoking" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const unredeemed = batch.codeCount - batch.redeemedCount

  const handleRevoke = async () => {
    setStep("revoking")
    setErrorMessage("")
    try {
      const result = await Api.revokeCodeBatch({
        skuId,
        batchId: batch.id,
        reason: reason.trim() || null,
        accessToken,
      })
      onRevoked(result.batch)
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "revoking") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Revoke Code Batch</h2>

        <p>
          This stops the batch's <strong>{unredeemed}</strong> unredeemed code{unredeemed === 1 ? "" : "s"} from
          being redeemed. Codes already redeemed are unaffected. You can undo this later.
        </p>
        <div className="generate-codes-form">
          <label className="generate-codes-field">
            <span className="generate-codes-label">Reason (optional)</span>
            <input
              type="text"
              maxLength={255}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="generate-codes-input"
              disabled={step === "revoking"}
              placeholder="e.g. codes leaked"
              autoFocus
            />
          </label>
        </div>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "revoking"}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={step === "revoking"}
            onClick={handleRevoke}
          >
            {step === "revoking" ? <><Spinner size={12} /> Revoking…</> : "Revoke"}
          </button>
        </div>
      </div>
    </dialog>
  )
}

// The Codes card, two tabs like the Orders card: Code Batches (admin-minted
// redemption code batches, with the Generate action) and Codes (one code's
// status by its value — the support question "what happened to this code?").
// ilokAvailable: the owner org has iLok switched on — the Generate dialog then
// offers the per-batch "allow iLok" toggle.
type CodesCardTab = "batches" | "lookup"

function CodesCard({ sku, versions, ilokAvailable, accessToken }: { sku: Sku, versions: SkuVersion[] | undefined, ilokAvailable: boolean, accessToken: string }) {
  const openGenerateCodes = useGenerateCodesModal()
  const [tab, setTab] = useState<CodesCardTab>("batches")
  // bumped after a mint so the list reloads from the top
  const [batchesVersion, setBatchesVersion] = useState(0)
  const mintable = sku.codePrefix !== null

  return (
    <TabCard
      key={sku.id}
      tabs={[{ key: "batches", label: "Code Batches" }, { key: "lookup", label: "Codes" }]}
      tab={tab}
      onTabChange={setTab}
      actions={tab === "batches" && (
        <button
          className="card-link"
          style={{
            background: "none", border: "none", padding: 0,
            cursor: mintable ? "pointer" : "default",
            opacity: mintable ? 1 : 0.5
          }}
          disabled={!mintable}
          title={mintable ? undefined : "This SKU has no code prefix — redemption codes cannot be minted"}
          // codes are customer-facing fulfillment — only released versions
          // are mintable (server-enforced too)
          onClick={() => openGenerateCodes(sku.id, (versions ?? []).filter(v => v.status === SkuVersionStatus.Released), () => setBatchesVersion(v => v + 1), { ilokAvailable })}
        >
          Generate
        </button>
      )}
    >
      {tab === "batches"
        ? <CodeBatchesTab sku={sku} accessToken={accessToken} reloadKey={batchesVersion} />
        : <CodeLookupTab sku={sku} accessToken={accessToken} />}
    </TabCard>
  )
}

// a code is the 4-char sku prefix + 16 z-base32 chars; the server applies the
// same normalization (see lib/api/code_value.ts), this copy just decides when
// the input is complete enough to send
const CODE_VALUE_LENGTH = 20
function normalizeCodeValue(input: string): string {
  const stripped = input.replace(/[-\s]/g, "")
  return stripped.substring(0, 4).toUpperCase() + stripped.substring(4).toLowerCase()
}
function formatCodeValue(value: string): string {
  return value.match(/.{1,4}/g)?.join("-") ?? value
}

// The Codes tab: one input, resolved once it holds a whole code; shorter
// input shows a hint instead (no partial searches — codes are secrets, the
// admin pastes the one in question). A found code collapses the input into a
// dismissible chip (the search-filter pattern) with the status pill beside
// it; the code's details follow below. The input keeps its text across tab
// switches only while the card is mounted, which is all a lookup needs.
function CodeLookupTab({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const [input, setInput] = useState("")
  const normalized = normalizeCodeValue(input)
  const value = normalized.length === CODE_VALUE_LENGTH ? normalized : null
  // undefined = loading, null = no such code for this sku
  const [result, setResult] = useState<RedemptionCodeLookup | null | undefined>(undefined)
  const [error, setError] = useState(false)
  const requestSeq = useRef(0)

  useEffect(() => {
    const seq = ++requestSeq.current
    setResult(undefined)
    setError(false)
    if (value === null) return
    Api.lookupRedemptionCode({ skuId: sku.id, code: value, accessToken })
      .then((r) => { if (seq === requestSeq.current) setResult(r) })
      .catch(() => { if (seq === requestSeq.current) setError(true) })
  }, [sku.id, accessToken, value])

  const found = value !== null && result != null ? result : null
  const status = found !== null ? codeStatus(found) : null

  return (
    <>
      <div className="filter-bar" style={{ marginTop: 0 }}>
        {found !== null && status !== null ? (
          <>
            <button
              className="skus-filter-chip skus-filter-chip-active icon-chip"
              style={{ fontFamily: "var(--al-mono-font-family)" }}
              onClick={() => setInput("")}
              title="Clear"
            >
              {formatCodeValue(found.code.code)} ✕
            </button>
            <span className="badge" style={{ cursor: "help" }} title={status.tooltip}>{status.label}</span>
          </>
        ) : (
          <input
            className="filter-input"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="Paste a code, e.g. XXXX-xxxx-xxxx-xxxx-xxxx"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            style={{ flex: "1 1 320px", fontFamily: input ? "var(--al-mono-font-family)" : undefined }}
          />
        )}
      </div>
      {value === null ? (
        <p className="empty-state">
          {input.trim() === ""
            ? "Shows whether a code was redeemed (and by whom), revoked, expired or is still open."
            : `${normalized.length} of ${CODE_VALUE_LENGTH} characters…`}
        </p>
      ) : error ? (
        <p className="empty-state">Error looking up the code</p>
      ) : result === undefined ? (
        <p className="empty-state"><Spinner size={16} /> Looking up code…</p>
      ) : result === null ? (
        <p className="empty-state">No code {formatCodeValue(value)} for this SKU.</p>
      ) : (
        <CodeLookupDetails lookup={result} />
      )}
    </>
  )
}

// One status for a looked-up code: redeemed wins over the batch's revocation,
// which wins over its expiry; otherwise the code is still open.
function codeStatus({ code, batch }: RedemptionCodeLookup): { label: string, tooltip: string } {
  if (code.redeemedAt !== null)
    return { label: "Redeemed", tooltip: `Redeemed ${new Date(code.redeemedAt).toLocaleDateString()}` }
  if (code.revokedAt !== null)
    return {
      label: "Revoked",
      tooltip: [
        `Revoked ${new Date(code.revokedAt).toLocaleDateString()}`,
        batch.revokedBy ? `by ${batch.revokedBy}` : null,
        batch.revokedReason ? `— ${batch.revokedReason}` : null,
      ].filter(Boolean).join(" "),
    }
  if (batch.expired)
    return { label: "Expired", tooltip: `Expired ${new Date(batch.expiresAt!).toLocaleDateString()}` }
  return { label: "Unredeemed", tooltip: "Not redeemed yet — can still be redeemed" }
}

// What is known about a looked-up code beyond its status: the batch it came
// from and — when redeemed — who redeemed it, with a link to the redemption
// detail (entitlement, licenses, activations).
function CodeLookupDetails({ lookup: { code, batch } }: { lookup: RedemptionCodeLookup }) {
  const redeemed = code.redeemedAt !== null
  const details: string[] = [
    batch.comment !== null ? `batch “${batch.comment}”` : "unnamed batch",
    `minted ${new Date(batch.createdAt).toLocaleDateString()}`,
  ]
  if (batch.skuVersion != null) details.push(`v${batch.skuVersion}`)
  if (batch.licenseType !== LicenseType.Standard) details.push(licenseTypeLabels[batch.licenseType])
  if (redeemed) details.push(`redeemed ${new Date(code.redeemedAt!).toLocaleDateString()}`)
  else if (code.expiresAt !== null && !batch.expired) details.push(`expires ${new Date(code.expiresAt).toLocaleDateString()}`)

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
        {details.join(" · ")}
      </span>
      {code.redeemedBy !== null && <UserChip userId={code.redeemedBy} />}
      {redeemed && (
        <Link className="card-link" style={{ fontSize: "10pt" }} to={`/redemptions/${code.id}`}>View redemption</Link>
      )}
    </div>
  )
}

// The Promotions tab of the Offerings card: every sale ever scheduled on the
// sku, newest window first, with where each stands (Scheduled / Running /
// Ended). Scheduled and running ones can be edited or deleted, a running one
// ended now; ended ones are history. The list is the tab's own fetch (like
// code batches), so a save reloads it without refetching the sku.
const promotionStatusLabels: Record<SkuPromotion["status"], string> = {
  scheduled: "Scheduled",
  running: "Running",
  ended: "Ended",
}

function formatPromotionMoment(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
}

function PromotionRow({ promotion, listPriceCents, onEdit, onEnd, onDelete }: {
  promotion: SkuPromotion
  listPriceCents: number | null
  onEdit: () => void
  onEnd: () => void
  onDelete: () => void
}) {
  const ended = promotion.status === "ended"
  const details: string[] = []
  // the discount as typed, with the other reading derived from the live
  // list price when there is one
  if (promotion.discountPercent !== null)
    details.push(listPriceCents !== null
      ? `${promotion.discountPercent}% off · ${formatPrice(listPriceCents * (1 - promotion.discountPercent / 100) / 100)}`
      : `${promotion.discountPercent}% off`)
  else if (promotion.priceCents !== null)
    details.push(listPriceCents !== null && promotion.priceCents < listPriceCents
      ? `${formatPrice(promotion.priceCents / 100)} · ${Math.round((1 - promotion.priceCents / listPriceCents) * 100)}% off`
      : listPriceCents !== null
        ? `${formatPrice(promotion.priceCents / 100)} · not below the list price — inactive`
        : formatPrice(promotion.priceCents / 100))
  details.push(promotion.endsAt !== null
    ? `${formatPromotionMoment(promotion.startsAt)} – ${formatPromotionMoment(promotion.endsAt)}`
    : `from ${formatPromotionMoment(promotion.startsAt)} · open-ended`)
  return (
    <div className="license-group" style={ended ? { opacity: 0.6 } : undefined}>
      <div className="product-group-header">
        <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {promotion.name ?? <span style={{ fontStyle: "italic", fontWeight: "normal", opacity: 0.7 }}>Sale</span>}
          <span className="badge">{promotionStatusLabels[promotion.status]}</span>
        </h2>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
            {details.join(" · ")}
          </span>
          {!ended && (
            <>
              <button className="card-link" style={rowActionStyle} onClick={onEdit}>Edit</button>
              {promotion.status === "running" && (
                <button className="card-link-danger" style={rowActionStyle} onClick={onEnd}>End now</button>
              )}
              <button className="card-link-danger" style={rowActionStyle} onClick={onDelete}>Delete</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// The Offerings card, two tabs: Offerings (what the sku sells — the page owns
// that list and its editor state, so the tab's header actions and body come
// in as props) and Promotions (time-bounded sales on those offerings, owned
// here). Each tab's header action is its own: Merchant dashboard + Add for
// offerings, Add for promotions.
type OfferingsCardTab = "offerings" | "promotions"

function OfferingsCard({ sku, accessToken, offeringsActions, children }: {
  sku: Sku
  accessToken: string
  offeringsActions: ReactNode
  children: ReactNode
}) {
  const [tab, setTab] = useState<OfferingsCardTab>("offerings")
  const [promotions, setPromotions] = useState<SkuPromotion[] | undefined>(undefined)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [editor, setEditor] = useState<{ promotion: SkuPromotion | null } | null>(null)
  const [pendingAction, setPendingAction] = useState<{ promotion: SkuPromotion, action: "end" | "delete" } | null>(null)
  // promotions price the sku's cheapest live one-time offering
  const listPriceCents = sku.offerings
    .filter(o => o.paymentType === "one_time" && o.discontinued !== true)
    .sort((a, b) => a.priceCents - b.priceCents)[0]?.priceCents ?? null

  // fetched when the tab is opened (and refetched on each return, so it is
  // never stale), not on every sku page load
  useEffect(() => {
    if (tab !== "promotions") return
    let cancelled = false
    setLoadError(null)
    Api.listPromotions({ skuId: sku.id, accessToken })
      .then(({ promotions }) => { if (!cancelled) setPromotions(promotions) })
      .catch(err => { if (!cancelled) setLoadError(err instanceof Error ? err.message : "unknown error") })
    return () => { cancelled = true }
  }, [sku.id, accessToken, reloadKey, tab])

  const reload = () => setReloadKey(k => k + 1)

  return (
    <>
    <TabCard
      key={sku.id}
      tabs={[{ key: "offerings", label: "Offerings" }, { key: "promotions", label: "Promotions" }]}
      tab={tab}
      onTabChange={setTab}
      actions={tab === "offerings" ? offeringsActions : (
        <button
          className="card-link"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
          onClick={() => setEditor({ promotion: null })}
        >
          Add
        </button>
      )}
    >
      {tab === "offerings" ? children : loadError !== null ? (
          <p className="empty-state">Error loading promotions: {loadError}</p>
        ) : promotions === undefined ? (
          <p className="empty-state"><Spinner size={16} /> Loading promotions…</p>
        ) : promotions.length === 0 ? (
          <p className="empty-state">No promotions — add one to put this SKU on sale for a while.</p>
        ) : (
          promotions.map(promotion => (
            <PromotionRow
              key={promotion.id}
              promotion={promotion}
              listPriceCents={listPriceCents}
              onEdit={() => setEditor({ promotion })}
              onEnd={() => setPendingAction({ promotion, action: "end" })}
              onDelete={() => setPendingAction({ promotion, action: "delete" })}
            />
          ))
        )}
    </TabCard>
      {editor && (
        <PromotionDialog
          skuId={sku.id}
          listPriceCents={listPriceCents}
          promotion={editor.promotion}
          accessToken={accessToken}
          onClose={() => setEditor(null)}
          onSaved={reload}
        />
      )}
      {pendingAction && (
        <PromotionActionDialog
          skuId={sku.id}
          promotion={pendingAction.promotion}
          action={pendingAction.action}
          accessToken={accessToken}
          onClose={() => setPendingAction(null)}
          onDone={reload}
        />
      )}
    </>
  )
}

// The Orders card has two tabs: Orders (the sku's store orders — signed-in
// purchases and claimed guest ones alike, each an ordinary order) and Guest
// orders (purchases made without an account, parked until the buyer's email
// claims them or support hands them to an account).
type OrdersCardTab = "orders" | "guestOrders"

function OrdersCard({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const [tab, setTab] = useState<OrdersCardTab>("orders")

  return (
    <TabCard
      key={sku.id}
      tabs={[{ key: "orders", label: "Orders" }, { key: "guestOrders", label: "Guest orders" }]}
      tab={tab}
      onTabChange={setTab}
    >
      {tab === "orders"
        ? <OrdersTab sku={sku} accessToken={accessToken} />
        : <GuestOrdersTab sku={sku} accessToken={accessToken} />}
    </TabCard>
  )
}

// One store order: who bought, when, the order (its detail page carries
// the refund state), what was paid, and the tags that explain the amount —
// an upgrade's total is the net price after trade-in, a guest purchase was
// parked until an account claimed it.
function OrderRow({ order }: { order: SkuOrder }) {
  const refunded = order.status === NfaOrderStatus.Refunded
  const details: ReactNode[] = [new Date(order.createdAt).toLocaleDateString()]
  details.push(<Link className="card-link" to={`/orders/${order.id}`}>order #{order.storeOrderNumber}</Link>)
  if (order.totalCents != null) details.push(formatPrice(order.totalCents / 100))
  details.push(<a className="card-link" href={order.receiptUrl} target="_blank" rel="noreferrer">receipt</a>)

  return (
    <div className="license-group" style={refunded ? { opacity: 0.6 } : undefined}>
      <div className="product-group-header">
        <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <UserChip userId={order.userId} />
          {order.upgrade && <span className="badge">Upgrade</span>}
          {order.guest && <span className="badge">Guest</span>}
          {refunded && <span className="badge">Refunded</span>}
        </h2>
        <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt", textAlign: "right" }}>
          {details.map((d, i) => <Fragment key={i}>{i > 0 && " · "}{d}</Fragment>)}
        </span>
      </div>
    </div>
  )
}

// The sku's store orders, newest first, with their headline counts and a
// search box that matches a buyer's email prefix (resolved to accounts
// server-side), a user or order id, or a store order number / id.
function OrdersTab({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const [stats, setStats] = useState<SkuOrderStats | null | undefined>(undefined)
  const [orders, setOrders] = useState<SkuOrder[] | undefined>(undefined)
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [searchText, setSearchText] = useState("")
  const requestSeq = useRef(0)

  const search = searchText.trim().length >= GUEST_ORDER_SEARCH_MIN_CHARS ? searchText.trim() : null

  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.listSkuOrders({
        skuId: sku.id,
        q: search,
        link: cursor,
        count: cursor === null ? CODES_PREVIEW_COUNT : CODES_PAGE_SIZE,
        accessToken,
      })
      if (seq !== requestSeq.current) return
      setOrders((prev) => (cursor === null ? page.orders : [...(prev ?? []), ...page.orders]))
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [sku.id, accessToken, search])

  useEffect(() => {
    setOrders(undefined)
    setLink(null)
    const timer = setTimeout(() => loadPage(null), search !== null ? GUEST_ORDER_SEARCH_DEBOUNCE_MS : 0)
    return () => clearTimeout(timer)
  }, [loadPage, search])

  useEffect(() => {
    let cancelled = false
    setStats(undefined)
    Api.getSkuOrderStats({ skuId: sku.id, accessToken })
      .then((r) => { if (!cancelled) setStats(r.stats) })
      .catch(() => { if (!cancelled) setStats(null) })
    return () => { cancelled = true }
  }, [sku.id, accessToken])

  return (
    <>
      <div className="filter-bar" style={{ marginTop: 0 }}>
        <input
          className="filter-input"
          type="text"
          autoComplete="off"
          placeholder="Search by email, order number or id…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: "1 1 260px" }}
        />
        <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt", whiteSpace: "nowrap" }}>
          {stats === undefined ? <Spinner size={12} /> : stats === null ? "" :
            `${stats.paid} paid · ${stats.refunded} refunded · ${formatPrice(stats.revenueCents / 100)}`}
        </span>
      </div>
      {error && orders === undefined ? (
        <p className="empty-state">Error loading orders</p>
      ) : orders === undefined ? (
        <p className="empty-state"><Spinner size={16} /> Loading orders…</p>
      ) : orders.length === 0 ? (
        <p className="empty-state">{search !== null ? "No orders match." : "No orders for this SKU yet."}</p>
      ) : (
        <>
          {orders.map((order) => (
            <OrderRow key={order.id} order={order} />
          ))}
          {(link !== null || error) && (
            <div style={{ marginTop: 8 }}>
              {link !== null && (
                loading ? <Spinner size={16} /> : (
                  <button
                    className="card-link"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => loadPage(link)}
                  >
                    More
                  </button>
                )
              )}
              {error && <p className="empty-state">Error loading more orders</p>}
            </div>
          )}
        </>
      )}
    </>
  )
}

function CodeBatchesTab({ sku, accessToken, reloadKey }: { sku: Sku, accessToken: string, reloadKey: number }) {
  const [batches, setBatches] = useState<RedemptionCodeBatch[] | undefined>(undefined)
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<RedemptionCodeBatch | null>(null)
  const [unrevokingId, setUnrevokingId] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState("")
  // stale responses (sku changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.listCodeBatches({
        skuId: sku.id,
        link: cursor,
        count: cursor === null ? CODES_PREVIEW_COUNT : CODES_PAGE_SIZE,
        accessToken,
      })
      if (seq !== requestSeq.current) return
      setBatches((prev) => (cursor === null ? page.batches : [...(prev ?? []), ...page.batches]))
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [sku.id, accessToken])

  useEffect(() => {
    setBatches(undefined)
    setLink(null)
    loadPage(null)
  }, [loadPage, reloadKey])

  const replaceBatch = (updated: RedemptionCodeBatch) =>
    setBatches((prev) => prev?.map((b) => (b.id === updated.id ? updated : b)))

  const handleUnrevoke = async (batch: RedemptionCodeBatch) => {
    setUnrevokingId(batch.id)
    setRevokeError("")
    try {
      const result = await Api.unrevokeCodeBatch({ skuId: sku.id, batchId: batch.id, accessToken })
      replaceBatch(result.batch)
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : "unknown error")
    } finally {
      setUnrevokingId(null)
    }
  }

  return (
    <>
      {error && batches === undefined ? (
        <p className="empty-state">Error loading code batches</p>
      ) : batches === undefined ? (
        <p className="empty-state"><Spinner size={16} /> Loading code batches…</p>
      ) : batches.length === 0 ? (
        <p className="empty-state">No codes have been generated for this SKU yet.</p>
      ) : (
        <>
          {batches.map((batch) => (
            <CodeBatchRow
              key={batch.id}
              batch={batch}
              onRevoke={() => setRevokeTarget(batch)}
              onUnrevoke={() => handleUnrevoke(batch)}
              unrevoking={unrevokingId === batch.id}
            />
          ))}
          {revokeError && <p className="empty-state">{revokeError}</p>}
          {(link !== null || error) && (
            <div style={{ marginTop: 8 }}>
              {link !== null && (
                loading ? <Spinner size={16} /> : (
                  <button
                    className="card-link"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => loadPage(link)}
                  >
                    More
                  </button>
                )
              )}
              {error && <p className="empty-state">Error loading more batches</p>}
            </div>
          )}
        </>
      )}
      {revokeTarget && (
        <RevokeBatchDialog
          batch={revokeTarget}
          skuId={sku.id}
          accessToken={accessToken}
          onClose={() => setRevokeTarget(null)}
          onRevoked={replaceBatch}
        />
      )}
    </>
  )
}

// One guest order: the address it is parked for, what was paid, the pinned
// version, and whether an account has claimed it (then it is an ordinary
// order — managed as one). A pending one carries support's two corrections
// as links (vendor admin, server-enforced): amend the address the buyer
// mistyped, or deposit the order into a chosen account outright. A refund
// (pending or claimed) still comes through LS / the order itself.
function GuestOrderRow({ guestOrder, accessToken, onChanged }: { guestOrder: GuestOrder, accessToken: string, onChanged: (guestOrder: GuestOrder) => void }) {
  const refunded = guestOrder.status === GuestOrderStatus.Refunded
  const claimed = guestOrder.status === GuestOrderStatus.Claimed
  const pending = guestOrder.status === GuestOrderStatus.Pending
  const [action, setAction] = useState<"amend" | "deposit" | null>(null)
  const details: ReactNode[] = [new Date(guestOrder.createdAt).toLocaleDateString()]
  details.push(<a className="card-link" href={guestOrder.receiptUrl} target="_blank" rel="noreferrer">order #{guestOrder.storeOrderNumber}</a>)
  details.push(formatPrice(guestOrder.totalCents / 100))
  if (guestOrder.skuVersion != null) details.push(`v${guestOrder.skuVersion}`)
  if (guestOrder.claimedAt !== null) details.push(`claimed ${new Date(guestOrder.claimedAt).toLocaleDateString()}`)
  else if (guestOrder.emailedAt === null) details.push("email not sent")

  return (
    <div className="license-group" style={refunded ? { opacity: 0.6 } : undefined}>
      <div className="product-group-header">
        <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{guestOrder.email}</span>
          {guestOrder.status === GuestOrderStatus.Pending && <span className="badge">Pending</span>}
          {claimed && <span className="badge">Claimed</span>}
          {refunded && <span className="badge">Refunded</span>}
        </h2>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
            {details.map((d, i) => <Fragment key={i}>{i > 0 && " · "}{d}</Fragment>)}
          </span>
          {guestOrder.userId !== null && <UserChip userId={guestOrder.userId} style={{ marginLeft: "auto" }} />}
          {pending && (
            <>
              <button className="card-link" style={linkButtonStyle} onClick={() => setAction("amend")}>Amend</button>
              <button className="card-link" style={linkButtonStyle} onClick={() => setAction("deposit")}>Deposit</button>
            </>
          )}
        </div>
      </div>
      {action === "amend" && (
        <AmendGuestOrderEmailDialog
          guestOrder={guestOrder}
          accessToken={accessToken}
          onClose={() => setAction(null)}
          onChanged={onChanged}
        />
      )}
      {action === "deposit" && (
        <DepositGuestOrderDialog
          guestOrder={guestOrder}
          accessToken={accessToken}
          onClose={() => setAction(null)}
          onChanged={onChanged}
        />
      )}
    </div>
  )
}

// a card-link rendered as a button (an action, not a navigation)
const linkButtonStyle: CSSProperties = { background: "none", border: "none", cursor: "pointer", padding: 0 }

const GUEST_ORDER_SEARCH_DEBOUNCE_MS = 300
const GUEST_ORDER_SEARCH_MIN_CHARS = 2

// Guest orders of the sku, newest first, with their headline counts and a
// search box that matches an email prefix, a user / order id or a store
// order number / id (the server tells which).
function GuestOrdersTab({ sku, accessToken }: { sku: Sku, accessToken: string }) {
  const [stats, setStats] = useState<GuestOrderStats | null | undefined>(undefined)
  const [guestOrders, setGuestOrders] = useState<GuestOrder[] | undefined>(undefined)
  const [link, setLink] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [searchText, setSearchText] = useState("")
  const requestSeq = useRef(0)

  const search = searchText.trim().length >= GUEST_ORDER_SEARCH_MIN_CHARS ? searchText.trim() : null

  const loadPage = useCallback(async (cursor: string | null) => {
    const seq = ++requestSeq.current
    setLoading(true)
    setError(false)
    try {
      const page = await Api.listGuestOrders({
        skuId: sku.id,
        q: search,
        link: cursor,
        count: cursor === null ? CODES_PREVIEW_COUNT : CODES_PAGE_SIZE,
        accessToken,
      })
      if (seq !== requestSeq.current) return
      setGuestOrders((prev) => (cursor === null ? page.guestOrders : [...(prev ?? []), ...page.guestOrders]))
      setLink(page.link)
    } catch {
      if (seq === requestSeq.current) setError(true)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [sku.id, accessToken, search])

  useEffect(() => {
    setGuestOrders(undefined)
    setLink(null)
    const timer = setTimeout(() => loadPage(null), search !== null ? GUEST_ORDER_SEARCH_DEBOUNCE_MS : 0)
    return () => clearTimeout(timer)
  }, [loadPage, search])

  // bumped after a row changes (a deposit moves it pending → claimed)
  const [statsSeq, setStatsSeq] = useState(0)
  useEffect(() => {
    let cancelled = false
    if (statsSeq === 0) setStats(undefined)
    Api.getGuestOrderStats({ skuId: sku.id, accessToken })
      .then((r) => { if (!cancelled) setStats(r.stats) })
      .catch(() => { if (!cancelled) setStats(null) })
    return () => { cancelled = true }
  }, [sku.id, accessToken, statsSeq])

  const replaceGuestOrder = (changed: GuestOrder) => {
    setGuestOrders((prev) => prev?.map((g) => (g.id === changed.id ? changed : g)))
    setStatsSeq((n) => n + 1)
  }

  return (
    <>
      <div className="filter-bar" style={{ marginTop: 0 }}>
        <input
          className="filter-input"
          type="text"
          autoComplete="off"
          placeholder="Search by email, order number or id…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: "1 1 260px" }}
        />
        <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt", whiteSpace: "nowrap" }}>
          {stats === undefined ? <Spinner size={12} /> : stats === null ? "" :
            `${stats.pending} pending · ${stats.claimed} claimed · ${stats.refunded} refunded · ${formatPrice(stats.revenueCents / 100)}`}
        </span>
      </div>
      {error && guestOrders === undefined ? (
        <p className="empty-state">Error loading guest orders</p>
      ) : guestOrders === undefined ? (
        <p className="empty-state"><Spinner size={16} /> Loading guest orders…</p>
      ) : guestOrders.length === 0 ? (
        <p className="empty-state">{search !== null ? "No guest orders match." : "No guest orders for this SKU yet."}</p>
      ) : (
        <>
          {guestOrders.map((guestOrder) => (
            <GuestOrderRow key={guestOrder.id} guestOrder={guestOrder} accessToken={accessToken} onChanged={replaceGuestOrder} />
          ))}
          {(link !== null || error) && (
            <div style={{ marginTop: 8 }}>
              {link !== null && (
                loading ? <Spinner size={16} /> : (
                  <button
                    className="card-link"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                    onClick={() => loadPage(link)}
                  >
                    More
                  </button>
                )
              )}
              {error && <p className="empty-state">Error loading more guest orders</p>}
            </div>
          )}
        </>
      )}
    </>
  )
}

// A version's claim set as one muted line: license claims by product name
// (marked when retroactive), topups as credits. Older versions' products may
// be absent from the redux cache — the id is an acceptable fallback on this
// admin-only, history surface (no per-product fetches).
function versionClaimSummary(version: SkuVersion, productEntities: RootState["products"]["entities"]): string {
  return (version.claims ?? [])
    .slice()
    .sort((a, b) => claimTypeOrder[a.type] - claimTypeOrder[b.type])
    .map(claim => claim.type === ClaimType.License
      ? `${productEntities[claim.productId]?.data?.name ?? claim.productId}${claim.retroactive ? " (retroactive)" : ""}`
      : `+${claim.amount} credits`)
    .join(" · ")
}

// Chart view of the release history: one vertical swimlane per plug-in
// (license claims only — credits stay in the list and Content views),
// versions stacked newest-on-top. Solid = the claim is in that version's own
// set; hatched = the version predates the claim but a NEWER version grants
// it retroactively (highest such version wins, mirroring the backend's
// entitled-set math). Reading a row therefore gives the exact holdings of
// that version's entitled users — whose count closes the row.
const LANE_SOLID = "rgba(0, 160, 176, 0.8)" // --nfa-blue
const LANE_HATCH = "repeating-linear-gradient(45deg, rgba(0, 160, 176, 0.55), rgba(0, 160, 176, 0.55) 5px, transparent 5px, transparent 10px)"
const LANE_ROW_HEIGHT = 34
// painted under the lane divs (inset shadows sit below children), so the
// separator never slices a continuous lane
const ROW_SEPARATOR = "inset 0 1px 0 rgba(255, 255, 255, 0.08)"

function VersionsChart({ versions, productEntities }: {
  versions: SkuVersion[]                 // newest first
  productEntities: RootState["products"]["entities"]
}) {
  const navigate = useNavigate()

  // lanes ordered by first appearance, oldest introductions on the left
  const laneProducts: string[] = []
  for (let i = versions.length - 1; i >= 0; i--)
    for (const claim of versions[i].claims ?? [])
      if (claim.type === ClaimType.License && !laneProducts.includes(claim.productId))
        laneProducts.push(claim.productId)

  if (laneProducts.length === 0)
    return <p className="empty-state">No version carries plug-ins yet — there is nothing to chart.</p>

  const cellFor = (versionIndex: number, productId: string): { claim: Claim, retroFrom: string | null } | null => {
    const own = (versions[versionIndex].claims ?? []).find(c => c.type === ClaimType.License && c.productId === productId)
    if (own) return { claim: own, retroFrom: null }
    // The two facts of a hatched cell come from DIFFERENT versions, because
    // copyClaimsToVersion carries the retroactive flag forward: the claim
    // whose terms apply is the NEWEST newer retroactive one (mirrors
    // computeEntitledClaims' highest-version-wins), while "retroactive from"
    // is the OLDEST — the version that introduced the grant, where the hatch
    // meets the solid lane.
    let claim: Claim | null = null
    let retroFrom: string | null = null
    for (let j = 0; j < versionIndex; j++) {
      // only RELEASED newer versions grant retroactively — a draft/beta's
      // retro flag is inert until it releases (computeEntitledClaims), so
      // painting its hatch here would promise deposits that haven't happened
      if (versions[j].status !== SkuVersionStatus.Released) continue
      const retro = (versions[j].claims ?? []).find(c => c.type === ClaimType.License && c.productId === productId && c.retroactive)
      if (retro) {
        claim = claim ?? retro
        retroFrom = versions[j].version
      }
    }
    return claim !== null ? { claim, retroFrom } : null
  }
  const cells = versions.map((_, i) => laneProducts.map(p => cellFor(i, p)))

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: `minmax(64px, auto) repeat(${laneProducts.length}, minmax(64px, 110px)) minmax(56px, auto)`,
        columnGap: 8,
      }}>
        <div />
        {laneProducts.map((productId) => {
          const product = productEntities[productId]?.data
          return (
            <div
              key={productId}
              title={product?.name ?? productId}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                padding: "0 4px 10px", textAlign: "center",
                cursor: product ? "pointer" : undefined
              }}
              onClick={product ? () => navigate(`/products/${productId}`) : undefined}
            >
              {product?.imageUrl ? (
                <img
                  src={product.imageUrl}
                  srcSet={buildImageSrcSet(product.imageUrl, product.availableWidths)}
                  sizes="28px"
                  alt=""
                  style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 4 }}
                />
              ) : (
                // CardImage's picture-icon placeholder, shrunk to thumbnail size
                <div aria-hidden="true" style={{
                  width: 28, height: 28, borderRadius: 4, background: "rgba(255, 255, 255, 0.08)",
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="var(--nfa-middle-grey)">
                    <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                  </svg>
                </div>
              )}
              <span style={{ fontSize: "9pt", lineHeight: 1.2 }}>{product?.name ?? productId}</span>
            </div>
          )
        })}
        <div />

        {versions.map((version, i) => {
          const separator = i > 0 ? ROW_SEPARATOR : undefined
          return (
            <Fragment key={version.id}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "10pt", whiteSpace: "nowrap", padding: "0 4px", boxShadow: separator }}>
                v{version.version}
                {version.status !== SkuVersionStatus.Released && (
                  <span style={{ color: "var(--nfa-light-grey)", fontSize: "8pt" }}>
                    {skuVersionStatusLabels[version.status].toLowerCase()}
                  </span>
                )}
              </div>
              {laneProducts.map((productId, k) => {
                const cell = cells[i][k]
                if (cell === null)
                  return <div key={productId} style={{ height: LANE_ROW_HEIGHT, boxShadow: separator }} />
                // lane runs are contiguous cells — round only the run's ends
                const roundTop = i === 0 || cells[i - 1][k] === null
                const roundBottom = i === versions.length - 1 || cells[i + 1][k] === null
                const product = productEntities[productId]?.data
                const terms = licenseClaimTerms(cell.claim).join(", ")
                const title = [
                  product?.name ?? productId,
                  cell.retroFrom !== null ? `retroactive from v${cell.retroFrom}` : null,
                  terms || null,
                ].filter(Boolean).join(" — ")
                // A gradient has no intrinsic size, so background-position only
                // works once the image is explicitly sized: stretch one hatch
                // image over the whole chart height and show each cell its own
                // slice — vertically neighboring retro cells join seamlessly.
                //
                // Unreleased rows read as their lifecycle stage: a DRAFT cell
                // is a dashed outline with no fill (planned, not yet real),
                // ALPHA a faint translucent, BETA a stronger translucent —
                // the visual outline → faint → translucent → solid mirrors
                // draft → alpha → beta → released. Retro hatches never appear
                // on unreleased rows (they are always the newest).
                const fillStyle: CSSProperties = cell.retroFrom !== null ? {
                  background: LANE_HATCH,
                  backgroundSize: `100% ${versions.length * LANE_ROW_HEIGHT}px`,
                  backgroundPosition: `0 ${-i * LANE_ROW_HEIGHT}px`,
                } : version.status === SkuVersionStatus.Draft ? {
                  background: "none",
                  border: "1px dashed rgba(0, 160, 176, 0.7)",
                  boxSizing: "border-box",
                } : version.status === SkuVersionStatus.Alpha ? {
                  background: "rgba(0, 160, 176, 0.2)",
                } : version.status === SkuVersionStatus.Beta ? {
                  background: "rgba(0, 160, 176, 0.4)",
                } : { background: LANE_SOLID }
                return (
                  <div key={productId} title={title} style={{ height: LANE_ROW_HEIGHT, display: "flex", justifyContent: "center", cursor: "help", boxShadow: separator }}>
                    <div style={{
                      width: "70%",
                      height: "100%",
                      ...fillStyle,
                      borderRadius: `${roundTop ? 6 : 0}px ${roundTop ? 6 : 0}px ${roundBottom ? 6 : 0}px ${roundBottom ? 6 : 0}px`,
                    }} />
                  </div>
                )
              })}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", color: "var(--nfa-light-grey)", fontSize: "10pt", whiteSpace: "nowrap", padding: "0 4px", boxShadow: separator }}>
                {version.entitledUserCount !== undefined &&
                  (version.entitledUserCount === 1 ? "1 user" : `${version.entitledUserCount} users`)}
              </div>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

function VersionRow({ version, latestReleased, productEntities, onEdit, onPromote, onDelete }: {
  version: SkuVersion
  // this row is the sku's latest RELEASED version (the fulfillment target)
  latestReleased: boolean
  productEntities: RootState["products"]["entities"]
  onEdit: () => void
  // no target = open the promote dialog on its stage chooser (draft/alpha
  // rows, where more than one stage is reachable)
  onPromote: (target?: SkuVersionStatus.Released) => void
  onDelete: () => void
}) {
  const date = new Date(version.createdAt).toLocaleDateString()
  const claimSummary = versionClaimSummary(version, productEntities)
  const draft = version.status === SkuVersionStatus.Draft
  const alpha = version.status === SkuVersionStatus.Alpha
  const beta = version.status === SkuVersionStatus.Beta
  const unreleased = draft || alpha || beta
  const badgeTitle = draft
    ? "Visible only to you — you hold a dev license to test it; editable and deletable with its records"
    : alpha
      ? "Your organization's members hold alpha licenses — still fully editable"
      : `Visible to your beta testers${version.betaAutoDeposit ? " (auto-deposits to new testers)" : ""} — still fully editable`
  return (
    <div className="license-group" style={draft ? { opacity: 0.75 } : undefined}>
      <div className="product-group-header">
        <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          v{version.version}
          {version.status !== SkuVersionStatus.Released && (
            <span className="badge" title={badgeTitle}>
              {skuVersionStatusLabels[version.status]}
            </span>
          )}
          {latestReleased && <span className="badge">Latest</span>}
        </h2>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>{date}</span>
          {/* the lifecycle actions: unreleased rows edit + move forward —
              one "Promote" opening the stage chooser while several stages
              are reachable, a plain "Release" on beta rows (the only stage
              left; a one-option chooser would be noise). Deletion:
              unreleased any time (cascades their records), released only
              the latest released. */}
          {unreleased && (
            <button className="card-link" style={rowActionStyle} onClick={onEdit}>Edit</button>
          )}
          {(draft || alpha) && (
            <button className="card-link" style={rowActionStyle} onClick={() => onPromote()}>Promote</button>
          )}
          {beta && (
            <button className="card-link" style={rowActionStyle} onClick={() => onPromote(SkuVersionStatus.Released)}>Release</button>
          )}
          {(unreleased || latestReleased) && (
            <button className="card-link-danger" style={rowActionStyle} onClick={onDelete}>Delete</button>
          )}
        </div>
      </div>
      {claimSummary !== "" && (
        <p style={{ margin: "6px 0 0", color: "var(--nfa-light-grey)", fontSize: "9pt" }}>
          {claimSummary}
        </p>
      )}
    </div>
  )
}

// The sku's versions, newest first (unpaged — dozens at most), each with its
// claim set and lifecycle status. Draft/beta rows are editable and promotable
// forward; released sets are immutable. No download links here — installers
// are downloaded from the user plane (the Library page); this card manages
// them via the add/edit/promote dialogs. The versions state lives in
// SkuPage: the Content card and the codes modal need it too.
function VersionsCard({ sku, versions, error, refresh, refreshClaims, refreshEntitlements, productEntities, isAdmin, accessToken }: {
  sku: Sku
  versions: SkuVersion[] | undefined
  error: boolean
  refresh: () => void
  refreshClaims: () => void
  // the page's Users section must reflect entitlement changes made from
  // this card: draft dev deposits, alpha/beta auto-deposits, release
  // dispositions, draft/beta deletion cascades
  refreshEntitlements: () => void
  productEntities: RootState["products"]["entities"]
  isAdmin: boolean
  accessToken: string
}) {
  const [adding, setAdding] = useState(false)
  const [editTarget, setEditTarget] = useState<SkuVersion | null>(null)
  const [promoteTarget, setPromoteTarget] = useState<{ version: SkuVersion, target?: SkuVersionStatus.Released } | null>(null)
  const [versionToDelete, setVersionToDelete] = useState<SkuVersion | null>(null)
  const [view, setView] = useState<"list" | "chart">("list")

  // the Add/Edit wizard's context: numbers must sort above the latest
  // RELEASED version and not collide with any row; the claim seed is the
  // latest release's set (or the newest row's while nothing is released)
  const latestReleased = versions?.find(v => v.status === SkuVersionStatus.Released) ?? null
  const wipVersion = versions?.find(v => v.status !== SkuVersionStatus.Released)
  const seedVersion = latestReleased ?? versions?.[0] ?? null
  const everShippedProducts = versions?.some(v =>
    v.status === SkuVersionStatus.Released && (v.claims ?? []).some(c => c.type === ClaimType.License)) ?? false

  // quiet cousin of the subscription page's billing-toggle: same segmented
  // shape, but greys only — it's a view preference, not a call to action
  const viewSegmentStyle = (active: boolean): CSSProperties => ({
    background: active ? "rgba(255, 255, 255, 0.12)" : "none",
    color: active ? "var(--nfa-off-white)" : "var(--nfa-light-grey)",
    border: "none", borderRadius: 4, padding: "2px 8px",
    fontSize: "9pt", cursor: "pointer",
    scale: "1" // pins the global button:hover scale
  })

  return (
    <div className="tab-card" style={{ marginTop: 32 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className="section-title tab-card-tab">Versions</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ display: "flex", gap: 2, background: "rgba(255, 255, 255, 0.06)", borderRadius: 6, padding: 2 }}>
            <button style={viewSegmentStyle(view === "list")} onClick={() => setView("list")}>List</button>
            <button style={viewSegmentStyle(view === "chart")} onClick={() => setView("chart")}>Chart</button>
          </div>
          {/* while a WIP exists over a released line, adding IS hotfixing
              (a second draft is refused server-side) — say so on the button */}
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            onClick={() => setAdding(true)}
          >
            {wipVersion !== undefined && latestReleased !== null ? "Hotfix" : "Add"}
          </button>
        </div>
      </div>
      <div className="tab-card-body">
        {error && versions === undefined ? (
          <p className="empty-state">Error loading versions</p>
        ) : versions === undefined ? (
          <p className="empty-state"><Spinner size={16} /> Loading versions…</p>
        ) : versions.length === 0 ? (
          <p className="empty-state">
            This SKU has no versions yet, so it carries no plug-ins and no
            downloads — Add its first draft version to define the plug-in set.
          </p>
        ) : view === "chart" ? (
          <VersionsChart versions={versions} productEntities={productEntities} />
        ) : (
          versions.map((version) => (
            <VersionRow
              key={version.id}
              version={version}
              latestReleased={version.id === latestReleased?.id}
              productEntities={productEntities}
              onEdit={() => setEditTarget(version)}
              onPromote={(target) => setPromoteTarget({ version, target })}
              onDelete={() => setVersionToDelete(version)}
            />
          ))
        )}
      </div>
      {adding && (
        <AddVersionDialog
          skuId={sku.id}
          skuOwnerId={sku.ownerId}
          latestVersion={latestReleased?.version ?? null}
          allVersionNumbers={(versions ?? []).map(v => ({ id: v.id, version: v.version }))}
          currentClaims={seedVersion?.claims ?? []}
          everHadProducts={everShippedProducts}
          wipVersion={wipVersion}
          productEntities={productEntities}
          isAdmin={isAdmin}
          accessToken={accessToken}
          onClose={() => setAdding(false)}
          onSaved={() => {
            // a released version's claim set is the sku's content now — both
            // cards must reflect it; a draft deposits the creator's dev
            // entitlement, so the Users section changes either way
            refresh()
            refreshClaims()
            refreshEntitlements()
          }}
        />
      )}
      {editTarget && (
        <AddVersionDialog
          skuId={sku.id}
          skuOwnerId={sku.ownerId}
          latestVersion={latestReleased?.version ?? null}
          allVersionNumbers={(versions ?? []).map(v => ({ id: v.id, version: v.version }))}
          currentClaims={editTarget.claims ?? []}
          everHadProducts={everShippedProducts}
          editVersion={editTarget}
          productEntities={productEntities}
          isAdmin={isAdmin}
          accessToken={accessToken}
          onClose={() => setEditTarget(null)}
          onSaved={refresh}
        />
      )}
      {promoteTarget && (
        <PromoteVersionDialog
          skuId={sku.id}
          version={promoteTarget.version}
          target={promoteTarget.target}
          previousReleased={latestReleased ?? undefined}
          skuShipsProducts={everShippedProducts}
          productEntities={productEntities}
          accessToken={accessToken}
          onClose={() => setPromoteTarget(null)}
          onDone={() => {
            // a release changes the sku's current content and store
            // presence; alpha/beta deposits and release dispositions change
            // the entitlement rows
            refresh()
            refreshClaims()
            refreshEntitlements()
          }}
        />
      )}
      {versionToDelete && (
        <DeleteVersionDialog
          skuId={sku.id}
          version={versionToDelete}
          accessToken={accessToken}
          onClose={() => setVersionToDelete(null)}
          onDone={() => {
            // deleting the latest release promotes the next one — its claims
            // become the sku's current content; a draft/beta delete cascades
            // its entitlements away
            refresh()
            refreshClaims()
            refreshEntitlements()
          }}
        />
      )}
    </div>
  )
}

const claimTypeOrder: Record<ClaimType, number> = {
  [ClaimType.License]: 0,
  [ClaimType.Topup]: 1,
}

// retroactivity is deliberately not shown here — it matters when comparing
// versions (the Versions card marks it), not on the current-content view
function licenseClaimTerms(claim: Claim): string[] {
  const terms: string[] = []
  if (claim.maxActivations != null) terms.push(`${claim.maxActivations} seats`)
  if (claim.maxYearlyActivations != null) terms.push(`${claim.maxYearlyActivations} activations/year`)
  return terms
}

function LicenseClaimTile({ claim, product }: { claim: Claim, product?: Product }) {
  const navigate = useNavigate()
  const terms = licenseClaimTerms(claim)

  return (
    <div
      className={"claim-tile" + (product ? " claim-tile-clickable" : "")}
      onClick={product ? () => navigate(`/products/${product.id}`) : undefined}
    >
      {/* a 16:9 placeholder keeps the tile shape for image-less products */}
      <CardImage
        imageUrl={product?.imageUrl}
        availableWidths={product?.availableWidths}
        alt={product?.name ?? "product"}
        placeholderAspect="16 / 9"
      />
      <div className="claim-tile-name">
        {product?.logoUrl ? (
          <img
            src={product.logoUrl}
            srcSet={buildImageSrcSet(product.logoUrl, product.logoAvailableWidths)}
            sizes="160px"
            alt={product.name}
            className="product-logo-section"
          />
        ) : product?.name ?? "License"}
      </div>
      {/* the tile itself navigates to the product page; no Remove — a
          version's claim set is immutable, the set changes with the next
          release (Add Version) */}
      {terms.length > 0 && (
        <div className="claim-tile-terms">{terms.join(" · ")}</div>
      )}
    </div>
  )
}

function AmountClaimTile({ label, amount }: { label: string, amount?: number }) {
  return (
    <div className="claim-tile">
      {/* the amount fills the image slot so the tile matches its neighbors */}
      <div className="claim-tile-amount">{amount != null ? `+${amount}` : ""}</div>
      <div className="claim-tile-name">{label}</div>
    </div>
  )
}

export default function SkuPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const [sku, setSku] = useState<Sku | undefined>(undefined)
  const [skuError, setSkuError] = useState(false)
  // the owner org's iLok switch (organizations.ilokEnabled, DB-set): the
  // grant / codes dialogs offer the per-issuance "allow iLok" toggle only
  // for orgs that have it; unknown/loading = off
  const ownerOrg = useOrganization(sku?.ownerId)
  const ilokAvailable = ownerOrg?.ilokEnabled === true
  const { data: claimIds, status: claimsStatus, refresh: refreshClaims } = useSkuClaims(id!, accessToken)
  const claimEntities = useSelector((state: RootState) => state.claims.entities)
  const productEntities = useSelector((state: RootState) => state.products.entities)
  const isAdmin = useSelector(meSelector)?.data?.role === UserRole.Admin

  const [editingSku, setEditingSku] = useState(false)
  const [deletingSku, setDeletingSku] = useState(false)
  const [offeringEditor, setOfferingEditor] = useState<{ offering: Offering | null } | null>(null)
  const [offeringToDelete, setOfferingToDelete] = useState<Offering | null>(null)
  const [granting, setGranting] = useState(false)
  // bumped after a grant: EntitlementsTable's state is local, remount reloads
  const [grantSeq, setGrantSeq] = useState(0)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [offeringError, setOfferingError] = useState("")

  // fetched directly instead of via the redux sku cache: that cache holds
  // store-surface skus, without the admin-only fields (and without
  // not-shipped skus at all); the offering dialogs refetch through this too
  const fetchSeq = useRef(0)
  const fetchSku = useCallback(async () => {
    if (!accessToken) return
    const seq = ++fetchSeq.current
    try {
      const { sku } = await Api.getAdminSku({ skuId: id!, accessToken })
      if (seq !== fetchSeq.current) return
      setSku(sku)
      setSkuError(false)
    } catch {
      if (seq === fetchSeq.current) setSkuError(true)
    }
  }, [id, accessToken])

  useEffect(() => {
    setSku(undefined)
    setSkuError(false)
    fetchSku()
  }, [fetchSku])

  // versions live at page level: the Versions card lists them, the Content
  // card is "the latest version's claims", and the codes modal picks one
  const [versions, setVersions] = useState<SkuVersion[] | undefined>(undefined)
  const [versionsError, setVersionsError] = useState(false)
  const versionsSeq = useRef(0)
  const loadVersions = useCallback(async () => {
    if (!accessToken) return
    const seq = ++versionsSeq.current
    setVersionsError(false)
    try {
      const result = await Api.listSkuVersions({ skuId: id!, accessToken })
      if (seq === versionsSeq.current) setVersions(result.versions)
    } catch {
      if (seq === versionsSeq.current) setVersionsError(true)
    }
  }, [id, accessToken])

  useEffect(() => {
    setVersions(undefined)
    loadVersions()
  }, [loadVersions])

  // Every version's claims render product names (row summaries, the chart,
  // the edit wizard's pre-fill) via the redux product cache — but the
  // Content card's claims fetch only covers the LATEST RELEASED set, so
  // products appearing only in draft/beta claims (typically another owner's)
  // would degrade to raw ids. Pull the missing ones in per id. Loop-safe:
  // the thunk's pending reducer creates the cache entry immediately, so the
  // undefined guard fires at most once per product.
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    if (versions === undefined || !accessToken) return
    const ids = new Set<string>()
    for (const v of versions) for (const c of v.claims ?? []) ids.add(c.productId)
    for (const productId of Array.from(ids))
      if (productEntities[productId] === undefined)
        dispatch(productFetchThunk({ id: productId, accessToken }))
  }, [versions, accessToken, dispatch, productEntities])

  const handleRestore = async (offering: Offering) => {
    setRestoringId(offering.id)
    setOfferingError("")
    try {
      await Api.updateOffering({ skuId: id!, offeringId: offering.id, patch: { discontinued: false }, accessToken })
      await fetchSku()
    } catch (err) {
      setOfferingError(err instanceof Error ? err.message : "unknown error")
    } finally {
      setRestoringId(null)
    }
  }

  const claims = (claimIds ?? [])
    .map((cid) => claimEntities[cid]?.data)
    .filter((c): c is Claim => c != null)
    .sort((a, b) => claimTypeOrder[a.type] - claimTypeOrder[b.type])

  const claimsLoading = claimsStatus === FetchableStateStatus.Initializing
  const licenseClaimCount = claims.filter((c) => c.type === ClaimType.License).length
  // the fulfillment target — grants and the Content card follow the latest
  // RELEASED version, not drafts or betas
  const latestReleasedVersion = versions?.find(v => v.status === SkuVersionStatus.Released)?.version ?? null
  const hasActiveOffering = sku?.offerings.some(o => o.discontinued !== true) ?? false

  if (skuError) {
    return (
      <div className="page">
        <p className="empty-state">Error loading SKU</p>
      </div>
    )
  }

  if (!sku) {
    return (
      <div className="page">
        <p className="empty-state"><Spinner size={16} /> Loading SKU…</p>
      </div>
    )
  }

  const offerings = [...sku.offerings].sort((a, b) =>
    Number(a.discontinued ?? false) - Number(b.discontinued ?? false)
    || offeringOrder[a.paymentType] - offeringOrder[b.paymentType]
    || a.priceCents - b.priceCents)
  // LemonSqueezy dashboard deep link — Admin only (Developers have no
  // dashboard access). Product-level: every offering of a sku lives under
  // the same store product, so the first recorded id is the sku's.
  const storeProductId = offerings.find(o => o.storeProductId != null)?.storeProductId
  const storeUrl = isAdmin && storeProductId != null
    ? `https://app.lemonsqueezy.com/products/${storeProductId}`
    : null
  // every version's license claims (drafts included) — the backend's "not
  // included in this SKU" rule for crossgrade products, and whether the sku
  // is a bundle at all (upgrade pricing mode)
  const claimedProductIds = Array.from(new Set((versions ?? []).flatMap(v => (v.claims ?? []).filter(c => c.type === ClaimType.License).map(c => c.productId))))
  const isBundle = claimedProductIds.length > 1

  return (
    <div className="page">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <h1 className="page-title" style={{ marginBottom: 4 }}><SkuName sku={sku} variant="title" /></h1>
          <CopyableId id={sku.id} />
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <button
            className="card-link"
            style={rowActionStyle}
            onClick={() => setEditingSku(true)}
          >
            Edit
          </button>
          <button
            className="card-link-danger"
            style={rowActionStyle}
            onClick={() => setDeletingSku(true)}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="license-group license-group-with-img license-group-plain">
        <div className="license-group-img-container">
          <CardImage imageUrl={sku.imageUrl} availableWidths={sku.availableWidths} alt={sku.name} />
        </div>
        <div className="product-group-header">
          <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
            {sku.codePrefix === undefined ? "" :
              sku.codePrefix === null ? "No code prefix — redemption codes cannot be minted" :
                <>Code prefix: <span style={{ color: "var(--nfa-off-white)" }}>{sku.codePrefix}</span></>}
          </span>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
            {/* store presence needs BOTH an active offering and a released
                version — flag the missing-release half here (the Offerings
                card already narrates the other) */}
            {versions !== undefined && latestReleasedVersion === null && hasActiveOffering && (
              <span className="badge" title="The store lists and sells only SKUs with a released version — release one from the Versions card">
                No release — not in store
              </span>
            )}
            {licenseClaimCount > 0 && (
              <span className="badge">
                {licenseClaimCount > 1 ? bundleIcon : singleIcon}
                {licenseClaimCount > 1 ? "Bundle" : "Single product"}
              </span>
            )}
            <OrgChip orgId={sku.ownerId} />
          </div>
        </div>
        {sku.description && (
          <p style={{
            margin: 0,
            color: "var(--nfa-light-grey)",
            fontSize: "10pt",
            gridArea: "list",
            padding: "0 20px 20px"
          }}>
            {sku.description}
          </p>
        )}
      </div>

      {sku.highlights.length > 0 && (
        <ul className="sku-highlights">
          {sku.highlights.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      )}

      <div className="tab-card" style={{ marginTop: 32 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* read-only: the sku's current content = the LATEST version's
              claims, and a version's set is immutable — it changes with the
              next release, defined in the Add Version dialog (older versions'
              frozen sets live in the Versions card) */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2 className="section-title tab-card-tab">Content</h2>
            {latestReleasedVersion !== null && (
              <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>of v{latestReleasedVersion}</span>
            )}
          </div>
        </div>
        <div className="tab-card-body">
          {claimsLoading ? (
            <p className="empty-state"><Spinner size={16} /> Loading plug-ins…</p>
          ) : claims.length === 0 ? (
            <p className="empty-state">
              {latestReleasedVersion === null
                ? "Nothing released yet — the SKU's content is its latest released version's claim set."
                : "The latest release has no plug-ins — the set is defined when adding a version."}
            </p>
          ) : (
            <div className="claim-tile-grid">
              {claims.map((claim) => {
                if (claim.type === ClaimType.License)
                  return <LicenseClaimTile key={claim.id} claim={claim} product={productEntities[claim.productId]?.data} />
                return <AmountClaimTile key={claim.id} label="Credits" amount={claim.amount} />
              })}
            </div>
          )}
        </div>
      </div>

      <VersionsCard
        sku={sku}
        versions={versions}
        error={versionsError}
        refresh={loadVersions}
        refreshClaims={refreshClaims}
        // EntitlementsTable keeps local state — the remount key is how the
        // page reloads it (the GrantSkuDialog pattern)
        refreshEntitlements={() => setGrantSeq(seq => seq + 1)}
        productEntities={productEntities}
        isAdmin={isAdmin}
        accessToken={accessToken}
      />

      <OfferingsCard
        sku={sku}
        accessToken={accessToken}
        offeringsActions={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {storeUrl && (
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="card-link"
                title="Go to merchant dashboard"
              >
                Merchant dashboard
              </a>
            )}
            <button
              className="card-link"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              onClick={() => setOfferingEditor({ offering: null })}
            >
              Add
            </button>
          </div>
        }
      >
        {offeringError && (
          <p className="generate-codes-error">{offeringError}</p>
        )}
        {offerings.length === 0 ? (
          <p className="empty-state">This SKU has no offerings — it is not shipped.</p>
        ) : (
          offerings.map((offering) => (
            <OfferingRow
              key={offering.id}
              offering={offering}
              isBundle={isBundle}
              onEdit={() => setOfferingEditor({ offering })}
              onDelete={() => setOfferingToDelete(offering)}
              onRestore={() => handleRestore(offering)}
              restoring={restoringId === offering.id}
            />
          ))
        )}
      </OfferingsCard>

      <OrdersCard sku={sku} accessToken={accessToken} />

      <CodesCard sku={sku} versions={versions} ilokAvailable={ilokAvailable} accessToken={accessToken} />

      <div className="tab-card" style={{ marginTop: 32 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h2 className="section-title tab-card-tab">Users</h2>
          <button
            className="card-link"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
            onClick={() => setGranting(true)}
          >
            Grant
          </button>
        </div>
        <div className="tab-card-body">
          <EntitlementsTable
            key={grantSeq}
            skuId={sku.id}
            showUserColumn
            previewCount={USERS_PREVIEW_COUNT}
            morePath={`/skus/${sku.id}/users`}
            accessToken={accessToken}
          />
        </div>
      </div>

      {deletingSku && (
        <DeleteSkuDialog
          sku={sku}
          accessToken={accessToken}
          onClose={() => setDeletingSku(false)}
          onDeleted={() => navigate("/skus")}
        />
      )}
      {editingSku && (
        <EditSkuDialog
          sku={sku}
          hasReleasedVersions={latestReleasedVersion !== null}
          accessToken={accessToken}
          onClose={() => setEditingSku(false)}
          onSaved={fetchSku}
        />
      )}
      {offeringEditor && (
        <OfferingDialog
          skuId={sku.id}
          skuOwnerId={sku.ownerId}
          claimedProductIds={claimedProductIds}
          offering={offeringEditor.offering}
          siblingOfferings={sku.offerings}
          accessToken={accessToken}
          onClose={() => setOfferingEditor(null)}
          onSaved={fetchSku}
        />
      )}
      {granting && (
        <GrantSkuDialog
          skuId={sku.id}
          latestVersion={latestReleasedVersion}
          ilokAvailable={ilokAvailable}
          accessToken={accessToken}
          onClose={() => setGranting(false)}
          onGranted={() => setGrantSeq(seq => seq + 1)}
        />
      )}
      {offeringToDelete && (
        <DeleteOfferingDialog
          skuId={sku.id}
          offering={offeringToDelete}
          accessToken={accessToken}
          onClose={() => setOfferingToDelete(null)}
          onDone={fetchSku}
        />
      )}
    </div>
  )
}
